import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  useMovimientosIngrediente,
  useRevertirSalidaBodega,
} from '../hooks/useIngredientes';
import { useToast } from '../hooks/useToast';
import ModalRevertirSalida from './ModalRevertirSalida';
import type { MovimientoStock } from '../services/ingredientesService';
import { etiquetaMovimiento } from '@suminuta/shared';
import type { MovimientosFilters } from '../services/ingredientesService';
import {
  TrendingUp,
  TrendingDown,
  Package,
  Filter as FilterIcon,
  X,
  ChevronLeft,
  ChevronRight,
  Activity,
  Undo2,
} from 'lucide-react';

interface TablaMovimientosStockProps {
  ingredienteId: number;
  onVerOrdenCompra?: (ordenId: string) => void;
}

const TablaMovimientosStock: React.FC<TablaMovimientosStockProps> = ({
  ingredienteId,
  onVerOrdenCompra,
}) => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<
    Omit<MovimientosFilters, 'page' | 'limit'>
  >({});
  const [showFilters, setShowFilters] = useState(false);

  // Deshacer una salida de bodega (PROMPT_N°051). Hasta acá un error de
  // tipeo sólo se arreglaba entrando a la base a mano.
  const [salidaARevertir, setSalidaARevertir] =
    useState<MovimientoStock | null>(null);
  const revertirMutation = useRevertirSalidaBodega();
  const { success, error: showError } = useToast();

  const { data, isLoading, error } = useMovimientosIngrediente(ingredienteId, {
    ...filters,
    page,
    limit: 10,
  });

  // Debug temporal: verificar datos del gráfico (ANTES de returns condicionales)
  React.useEffect(() => {
    if (data?.grafico && data.grafico.length > 0) {
      console.log('📊 Total puntos del gráfico:', data.grafico.length);
      const puntosHoy = data.grafico.filter((g) =>
        g.fecha.includes('2025-10-07')
      );
      console.log('📌 Puntos del 07-10-2025:', puntosHoy.length);
      puntosHoy.forEach((p, i) => {
        console.log(
          `  Punto ${i + 1}: ${new Date(p.fecha).toLocaleTimeString('es-CL')} -> Stock: ${p.stock}, tipo: ${p.tipo}, cantidad: ${p.cantidad}`
        );
      });
    }
  }, [data?.grafico]);

  const handleRevertir = async (motivo: string) => {
    if (!salidaARevertir) return;
    try {
      const r = await revertirMutation.mutateAsync({
        id: ingredienteId,
        movimientoId: salidaARevertir.id,
        motivo,
      });
      setSalidaARevertir(null);
      success(
        'Salida deshecha',
        `Se devolvieron ${r.reverso.cantidad_devuelta} ${r.reverso.unidad}.`
      );
    } catch (e: any) {
      const payload = e?.response?.data;
      showError(
        'No se pudo deshacer la salida',
        payload?.message || 'Intentá nuevamente.'
      );
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Cargando movimientos...
        </span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">
          Error al cargar movimientos
        </p>
      </div>
    );
  }

  // No data
  if (!data) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-blue-800 dark:text-blue-200">
          No hay datos disponibles
        </p>
      </div>
    );
  }

  const { movimientos, estadisticas, grafico, pagination } = data;

  // Helper para formatear fecha (solo fecha sin hora para el gráfico)
  const formatFecha = (fecha: string) => {
    const date = new Date(fecha);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper para formatear fecha con hora completa para el gráfico
  const formatFechaConHora = (fecha: string) => {
    return new Intl.DateTimeFormat('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(fecha));
  };

  // Helper para formatear fecha con hora para la tabla
  const formatFechaTabla = (fecha: string) => {
    return new Intl.DateTimeFormat('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(fecha));
  };

  // Helper para formatear cantidad con signo para la tabla
  const formatCantidadTabla = (cantidad: number, unidad: string) => {
    const signo = cantidad >= 0 ? '+' : '';
    return `${signo}${cantidad} ${unidad}`;
  };

  // Helper para formatear cantidad con tipo para el gráfico
  const formatCantidadGrafico = (cantidad: number, tipo: string) => {
    const signo = tipo === 'entrada' ? '+' : tipo === 'salida' ? '-' : '±';
    return `${signo}${Math.abs(cantidad)}`;
  };

  // Componente de tooltip personalizado para el gráfico
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const unidadMedida = data.unidad_medida || '';
      const fechaParaFormato = data.fechaCompleta || data.fecha; // Usar fechaCompleta si existe
      return (
        <div className="bg-gray-900 dark:bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-xs text-gray-300 mb-1">
            {formatFechaConHora(fechaParaFormato)}
          </p>
          <p className="text-sm font-bold text-blue-400">
            Stock: {data.stock} {unidadMedida}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            <span
              className={
                data.tipo === 'entrada'
                  ? 'text-green-400'
                  : data.tipo === 'salida'
                    ? 'text-red-400'
                    : 'text-yellow-400'
              }
            >
              {formatCantidadGrafico(data.cantidad, data.tipo)} {unidadMedida}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Helper para obtener clase de badge según tipo
  const getTipoBadgeClass = (tipo: string) => {
    const classes = {
      entrada:
        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      salida: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      ajuste:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    };
    return classes[tipo as keyof typeof classes] || 'bg-gray-100 text-gray-800';
  };

  // Helper para obtener badge de motivo.
  //
  // El mapa vivía acá y el Dashboard tenía otro propio que etiquetaba por
  // `tipo`: una salida de bodega salía como "Salida por producción". Ahora
  // los tres consumidores —esta tabla, el kardex y el Dashboard— leen
  // `@suminuta/shared/movimientos` (PROMPT_N°053).
  const getMotivoBadge = (motivo: string) => {
    const et = etiquetaMovimiento(motivo);
    const clases: Record<string, string> = {
      compra: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      produccion:
        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      ajuste:
        'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      merma:
        'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      bodega:
        'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      reverso: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${clases[et.tono] ?? clases.ajuste}`}
      >
        <span className="mr-1">{et.emoji}</span>
        {et.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Título */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
          Historial de Movimientos de Stock
        </h3>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <FilterIcon className="h-4 w-4 mr-1.5" />
          Filtros
        </button>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.tipo || ''}
                onChange={(e) => handleFilterChange('tipo', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="entrada">Entrada</option>
                <option value="salida">Salida</option>
                <option value="ajuste">Ajuste</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.motivo || ''}
                onChange={(e) => handleFilterChange('motivo', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="orden_compra">Orden de Compra</option>
                <option value="produccion_cocina">Producción Cocina</option>
                <option value="merma">Merma</option>
                <option value="inventario">Inventario</option>
                <option value="devolucion">Devolución</option>
                <option value="entrada_bodega">Entrada de bodega</option>
                <option value="salida_bodega">Salida de bodega</option>
                <option value="reverso_salida_bodega">
                  Reverso de salida
                </option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Desde
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.fecha_inicio || ''}
                onChange={(e) =>
                  handleFilterChange('fecha_inicio', e.target.value)
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hasta
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.fecha_fin || ''}
                onChange={(e) =>
                  handleFilterChange('fecha_fin', e.target.value)
                }
              />
            </div>
          </div>

          {Object.keys(filters).length > 0 && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 mr-1" />
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Estadísticas Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Movimientos
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {estadisticas.total_movimientos}
              </p>
            </div>
            <Package className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">
                Entradas
              </p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                {estadisticas.entradas.cantidad}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">
                +{estadisticas.entradas.total}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 dark:text-red-300">Salidas</p>
              <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                {estadisticas.salidas.cantidad}
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">
                {estadisticas.salidas.total}
              </p>
            </div>
            <TrendingDown className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Ajustes
              </p>
              <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                {estadisticas.ajustes.cantidad}
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                {estadisticas.ajustes.total > 0 ? '+' : ''}
                {estadisticas.ajustes.total}
              </p>
            </div>
            <Activity className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Gráfico de Evolución */}
      {grafico && grafico.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Evolución del Stock (Últimos 30 días)
          </h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={grafico.map((item) => ({
                ...item,
                timestamp: new Date(item.fecha).getTime(), // ID único numérico
                fechaDisplay: formatFecha(item.fecha),
                fechaCompleta: item.fecha, // Mantener fecha completa
                unidad_medida: data?.ingrediente.unidad_medida || '',
              }))}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#374151"
                opacity={0.1}
              />
              <XAxis
                dataKey="timestamp"
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
                interval="preserveStartEnd"
                tickFormatter={(timestamp) => {
                  const date = new Date(timestamp);
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  return `${day}-${month}`;
                }}
              />
              <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                type="monotone"
                dataKey="stock"
                name="Stock"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={(props: any) => {
                  const { cx, cy, payload, index } = props;
                  // Resaltar puntos según tipo de movimiento
                  const fillColor =
                    payload.tipo === 'entrada'
                      ? '#10B981' // verde
                      : payload.tipo === 'salida'
                        ? '#EF4444' // rojo
                        : '#F59E0B'; // amarillo
                  return (
                    <circle
                      key={`dot-${payload.timestamp || index}`}
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill={fillColor}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabla de Movimientos */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Motivo
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cantidad
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Stock Anterior
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Stock Nuevo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Detalles
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {movimientos.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No hay movimientos para mostrar
                  </td>
                </tr>
              ) : (
                movimientos.map((mov) => (
                  <tr
                    key={mov.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {formatFechaTabla(mov.fecha)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoBadgeClass(mov.tipo)}`}
                      >
                        {mov.tipo.charAt(0).toUpperCase() + mov.tipo.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getMotivoBadge(mov.motivo)}
                    </td>
                    <td
                      className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                        mov.cantidad >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {formatCantidadTabla(mov.cantidad, mov.unidad_medida)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                      {mov.stock_anterior} {mov.unidad_medida}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                      {mov.stock_nuevo} {mov.unidad_medida}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {mov.usuario.nombre}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="space-y-1">
                        {mov.orden_compra && (
                          <button
                            onClick={() =>
                              onVerOrdenCompra?.(
                                mov.orden_compra!.id.toString()
                              )
                            }
                            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                          >
                            #{mov.orden_compra.numero_orden}
                          </button>
                        )}
                        {mov.receta && (
                          <div className="text-xs text-purple-600 dark:text-purple-400">
                            Receta: {mov.receta.nombre}
                          </div>
                        )}
                        {mov.pedido && (
                          <div className="text-xs text-indigo-600 dark:text-indigo-400">
                            Pedido: {mov.pedido.codigo}
                          </div>
                        )}
                        {mov.merma && (
                          <div className="text-xs text-orange-600 dark:text-orange-400">
                            Merma ID: {mov.merma.id}
                          </div>
                        )}
                        {/* En una salida de bodega el destino es el dato
                            que importa, y viaja dentro de las notas:
                            "<Motivo>: <destino> - <notas>". */}
                        {mov.motivo === 'salida_bodega' && mov.notas && (
                          <div className="text-xs text-amber-700 dark:text-amber-400">
                            {mov.notas}
                          </div>
                        )}
                        {mov.motivo === 'entrada_bodega' && mov.notas && (
                          <div className="text-xs text-emerald-700 dark:text-emerald-400">
                            {mov.notas}
                          </div>
                        )}
                        {mov.motivo === 'salida_bodega' &&
                          mov.revertido_por && (
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              Revertida
                            </span>
                          )}
                        {mov.motivo === 'reverso_salida_bodega' && (
                          <div className="text-xs text-blue-700 dark:text-blue-400">
                            {mov.notas}
                            {mov.movimiento_revertido_id && (
                              <span className="ml-1 text-gray-500 dark:text-gray-400">
                                (deshace #{mov.movimiento_revertido_id})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {/* Sólo en salidas de bodega que todavía no se
                          deshicieron. El backend rechaza el resto igual. */}
                      {mov.motivo === 'salida_bodega' &&
                        !mov.revertido_por && (
                          <button
                            type="button"
                            title="Deshacer esta salida"
                            onClick={() => setSalidaARevertir(mov)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                          >
                            <Undo2 className="h-3.5 w-3.5" />
                            Deshacer
                          </button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination && pagination.totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrando{' '}
                  <span className="font-medium">{(page - 1) * 10 + 1}</span> -{' '}
                  <span className="font-medium">
                    {Math.min(page * 10, pagination.total)}
                  </span>{' '}
                  de <span className="font-medium">{pagination.total}</span>{' '}
                  movimientos
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Página {page} de {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalRevertirSalida
        isOpen={!!salidaARevertir}
        onClose={() => setSalidaARevertir(null)}
        movimiento={salidaARevertir}
        nombreIngrediente={data?.ingrediente.nombre ?? ''}
        onConfirm={handleRevertir}
        isLoading={revertirMutation.isPending}
      />
    </div>
  );
};

export default TablaMovimientosStock;
